import { getGenerativeModel } from 'firebase/ai';
import { vertexAI } from '../firebaseConfig';
import { AIQuoteResponse, MultiCarrierResult, CarrierBreakdown } from '../types';

const QUOTE_PROMPT = (userInput: string, companyName: string) => `
You are an expert telecommunications sales consultant and business solutions advisor for a multi-carrier telecom partner.

Analyze the following business requirements and generate a comprehensive quote configuration:

Company: ${companyName}
Requirements: ${userInput}

When pricing devices or plans, use Google Search to verify current, real pricing and availability directly from the carrier's official website before including any number or device in the quote. If you cannot verify a specific device or promotion is currently offered by the carrier, do NOT include it — instead flag it in the response as "needsVerification": true with a note explaining what couldn't be confirmed, rather than guessing or inventing a price.

Return ONLY valid JSON (no markdown, no explanation) matching this exact structure:
{
  "items": [
    {
      "type": "Voice|Business Internet|IoT / Telematics|Tablet / Mobile Data|Professional Installation",
      "planName": "string",
      "lines": <integer>,
      "mrc": <number per line/unit>,
      "needsVerification": <boolean>,
      "verificationNote": "string if needs verification",
      "devices": [
        { "name": "device name", "cost": <monthly cost>, "count": <integer>, "needsVerification": <boolean>, "verificationNote": "string" }
      ]
    }
  ],
  "recommendedPromos": [
    {
      "name": "promo name",
      "type": "monthly|onetime",
      "value": <dollar amount>,
      "duration": <months if monthly>,
      "lines": <lines if applicable>,
      "needsVerification": <boolean>,
      "verificationNote": "string"
    }
  ],
  "summary": "2-3 sentence executive summary of the recommended solution",
  "analysis": {
    "usagePatterns": { "primaryUse": "string", "dataNeeds": "string", "mobilityLevel": "string" },
    "financials": { "estimatedCurrentSpend": <number>, "projectedSavings": <number>, "paybackMonths": <number> }
  },
  "insights": {
    "narrative": "3-4 sentence compelling executive narrative about why this solution transforms their business",
    "objectionHandling": [
      "Objection: [common objection] | Response: [compelling answer]",
      "Objection: [common objection] | Response: [compelling answer]",
      "Objection: [common objection] | Response: [compelling answer]"
    ],
    "industryBenefits": [
      "Specific benefit 1 with quantifiable outcome",
      "Specific benefit 2 with quantifiable outcome",
      "Specific benefit 3 with quantifiable outcome",
      "Specific benefit 4 with quantifiable outcome"
    ],
    "roiSlicing": "Detailed ROI calculation showing total investment vs returns over contract term",
    "carrierComparison": [
      { "carrier": "Verizon", "strength": "Network reliability in rural areas", "weakness": "Higher cost per line, less flexible business plans", "score": 72 },
      { "carrier": "AT&T", "strength": "FirstNet public safety network", "weakness": "Limited international options, higher device costs", "score": 68 },
      { "carrier": "Proposed Solution", "strength": "Best overall value and support for this customer profile", "weakness": "Evaluate coverage in customer's specific service area", "score": 91 }
    ]
  }
}
`;

function extractJSON(text: string): unknown {
  const clean = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
  const match = clean.match(/\{[\s\S]*\}/);
  if (match) return JSON.parse(match[0]);
  throw new Error('No valid JSON found in response');
}

export const generateQuoteConfig = async (
  userInput:   string,
  companyName: string = 'this organization',
  files?:      { base64: string; mimeType: string }[]
): Promise<AIQuoteResponse | null> => {
  const model = getGenerativeModel(vertexAI, { 
    model: 'gemini-3.6-flash',
    tools: [{ googleSearch: {} }] 
  });

  const parts: Parameters<typeof model.generateContent>[0] extends (infer U)[] ? U[] : never[] = [];

  if (files?.length) {
    for (const f of files) {
      (parts as unknown[]).push({ inlineData: { mimeType: f.mimeType, data: f.base64 } });
    }
  }

  (parts as unknown[]).push({ text: QUOTE_PROMPT(userInput, companyName) });

  const result = await model.generateContent(parts as Parameters<typeof model.generateContent>[0]);
  const text   = result.response.text();
  return extractJSON(text) as AIQuoteResponse;
};

export const analyzeBills = async (
  files: { base64: string; mimeType: string }[]
): Promise<MultiCarrierResult | null> => {
  if (!files || files.length === 0) return null;

  const endpoint = "https://analyzebill-ibh2b2zgea-uc.a.run.app";
  const result: MultiCarrierResult = {
    carriers: [],
    combined: {
      monthlyServiceTotal: 0,
      monthlyHardwareTotal: 0,
      taxesAndFees: 0,
      etfTotal: 0,
      totalDue: 0,
      deviceCounts: { phone: 0, tablet: 0, watch: 0, connected: 0, other: 0 },
    },
  };

  for (const file of files) {
    try {
      const base64Data = file.base64.includes(',') ? file.base64.split(',')[1] : file.base64;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fileData: base64Data,
          mimeType: file.mimeType,
          carrier: null 
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Cloud function returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      const inputs = data.calculatorInputs;
      if (!inputs) {
          throw new Error("Invalid response format from analyzeBill endpoint.");
      }

      const priorAdjustment = inputs.priorPeriodAdjustment ?? 0;
      const otherCredits    = inputs.otherOneTimeCredits ?? 0;
      
      const billTotalDue = inputs.compTotalMonthly ?? 
        ((inputs.compMonthlyService || 0) + 
         (inputs.compMonthlyHW || 0) + 
         (inputs.compTaxesFees || 0) - 
         priorAdjustment - 
         otherCredits);

      const counts = {
        phone: inputs.voiceLines || 0,
        tablet: inputs.tabletLines || 0,
        watch: inputs.watchLines || 0,
        connected: (inputs.hotspotLines || 0) + (inputs.hsiLines || 0),
        other: inputs.otherLines || 0
      };

      const breakdown: CarrierBreakdown = {
        providerName: inputs.compName || "Unknown Carrier",
        monthlyServiceTotal: inputs.compMonthlyService || 0,
        monthlyHardwareTotal: inputs.compMonthlyHW || 0,
        taxesAndFees: inputs.compTaxesFees || 0,
        priorPeriodAdjustment: priorAdjustment,
        otherOneTimeCredits: otherCredits,
        etfTotal: 0, // Not provided by analyzeBill backend directly yet
        totalDue: billTotalDue,
        deviceCounts: counts,
        needsReview: inputs.needsReview || false,
        reviewReason: inputs.reviewReason || ""
      };
      
      result.carriers.push(breakdown);

      result.combined.monthlyServiceTotal += breakdown.monthlyServiceTotal;
      result.combined.monthlyHardwareTotal += breakdown.monthlyHardwareTotal;
      result.combined.taxesAndFees += breakdown.taxesAndFees;
      result.combined.etfTotal += breakdown.etfTotal;
      result.combined.totalDue += breakdown.totalDue;
      
      result.combined.deviceCounts.phone += counts.phone;
      result.combined.deviceCounts.tablet += counts.tablet;
      result.combined.deviceCounts.watch += counts.watch;
      result.combined.deviceCounts.connected += counts.connected;
      result.combined.deviceCounts.other += counts.other;

    } catch (err) {
      console.error("Failed to process bill file:", err);
      
      result.carriers.push({
        providerName: "Unknown (extraction failed)",
        monthlyServiceTotal: 0,
        monthlyHardwareTotal: 0,
        taxesAndFees: 0,
        priorPeriodAdjustment: 0,
        otherOneTimeCredits: 0,
        etfTotal: 0,
        totalDue: 0,
        deviceCounts: { phone: 0, tablet: 0, watch: 0, connected: 0, other: 0 },
        needsReview: true,
        reviewReason: "This bill could not be processed and was excluded from totals.",
        failed: true,
        errorMessage: err instanceof Error ? err.message : String(err),
      });
      continue;
    }
  }

  return result;
};
